import { sortAppsByOrder } from "@/common/helpers/sort-apps-by-order";
import { useFilteredNavigation } from "@/common/hooks/use-navigation";
import { CommandButton } from "@/components/command-palette/command-button";
import { ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NavigationItem } from "@/config/navigation/types";
import { userConfigStore } from "@/store/user-config-store";
import { RotateCcw } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SortAndToggleableList } from "./sort-and-toggleable-list";

export function AppsSection() {
  const { t } = useTranslation("user-settings");
  const appOrder = userConfigStore((state) => state.appOrder);
  const dashboardHiddenAppIds = userConfigStore((state) => state.dashboardHiddenAppIds);
  const setAppOrder = userConfigStore((state) => state.setAppOrder);
  const setDashboardHiddenAppIds = userConfigStore((state) => state.setDashboardHiddenAppIds);
  const isDefault = dashboardHiddenAppIds.length === 0 && appOrder.length === 0;

  const filteredNavigation = useFilteredNavigation();

  const orderedApps = useMemo(() => {
    const apps = filteredNavigation.filter((item) => item.id !== "home");
    return sortAppsByOrder(apps, appOrder);
  }, [filteredNavigation, appOrder]);

  function handleReorder(apps: NavigationItem[]) {
    setAppOrder(apps.map((app) => app.id));
  }

  function handleToggle(appId: string, hidden: boolean) {
    const next = hidden ? [...dashboardHiddenAppIds, appId] : dashboardHiddenAppIds.filter((id) => id !== appId);
    setDashboardHiddenAppIds(next);
  }

  function handleRestoreDefaults() {
    setAppOrder([]);
    setDashboardHiddenAppIds([]);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Apps")}</CardTitle>
        <p className="text-muted-foreground text-sm">{t("AppsDescription")}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {orderedApps.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("AppsNoApps")}</p>
        ) : (
          <SortAndToggleableList
            items={orderedApps}
            toggledOffIds={dashboardHiddenAppIds}
            onReorder={handleReorder}
            onToggle={handleToggle}
          />
        )}

        <div className="flex justify-end">
          <CommandButton
            commandId="user-settings-restore-apps-defaults"
            label={t("RestoreAppsDefaults")}
            icon={<RotateCcw />}
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={handleRestoreDefaults}
            disabled={isDefault}
          >
            {t("RestoreAppsDefaults")}
            <RotateCcw className="h-4 w-4" />
          </CommandButton>
        </div>
      </CardContent>
    </Card>
  );
}
